import { DrawingModel, type DrawStyle, defaultStyle } from "./DrawingModel";
import type { Chart } from "../chart/Chart";
import { TrendLine, Ray, ExtendedLine, Arrow, HorizontalLine, VerticalLine } from "./tools/lines";
import { Rectangle, Ellipse } from "./tools/shapes";
import { ParallelChannel } from "./tools/channels";
import { FibRetracement, FibExtension } from "./tools/fib";
import { PriceRange, DateRange, RiskReward } from "./tools/measure";
import { TextLabel } from "./tools/text";
import { Position } from "./tools/position";

export interface ToolDef {
  id: string;
  label: string;
  points: number; // anchors placed by user (1 or 2)
  make: (style: DrawStyle) => DrawingModel;
  // add derived anchors after the 2-point drag (channel/fibext/riskreward)
  initExtra?: (d: DrawingModel, chart: Chart) => void;
  needsText?: boolean;
}

export const TOOL_DEFS: Record<string, ToolDef> = {
  trendline: { id: "trendline", label: "Trend Line", points: 2, make: (s) => new TrendLine(s) },
  hline: { id: "hline", label: "Horizontal Line", points: 1, make: (s) => new HorizontalLine(s) },
  vline: { id: "vline", label: "Vertical Line", points: 1, make: (s) => new VerticalLine(s) },
  ray: { id: "ray", label: "Ray", points: 2, make: (s) => new Ray(s) },
  extended: { id: "extended", label: "Extended Line", points: 2, make: (s) => new ExtendedLine(s) },
  arrow: { id: "arrow", label: "Arrow", points: 2, make: (s) => new Arrow(s) },
  rect: { id: "rect", label: "Rectangle", points: 2, make: (s) => new Rectangle(s) },
  ellipse: { id: "ellipse", label: "Ellipse", points: 2, make: (s) => new Ellipse(s) },
  channel: {
    id: "channel",
    label: "Parallel Channel",
    points: 2,
    make: (s) => new ParallelChannel(s),
    initExtra: (d, chart) => {
      const [a, b] = d.anchors;
      const span = chart.viewport.priceMax - chart.viewport.priceMin;
      d.anchors.push({ time: b.time, price: b.price - span * 0.15 });
    },
  },
  fibretr: { id: "fibretr", label: "Fib Retracement", points: 2, make: (s) => new FibRetracement(s) },
  fibext: {
    id: "fibext",
    label: "Fib Extension",
    points: 2,
    make: (s) => new FibExtension(s),
    initExtra: (d) => {
      const b = d.anchors[1];
      d.anchors.push({ time: b.time, price: b.price });
    },
  },
  pricerange: { id: "pricerange", label: "Price Range", points: 2, make: (s) => new PriceRange(s) },
  daterange: { id: "daterange", label: "Date & Time Range", points: 2, make: (s) => new DateRange(s) },
  riskreward: {
    id: "riskreward",
    label: "Risk / Reward",
    points: 2,
    make: (s) => new RiskReward(s),
    initExtra: (d) => {
      const [entry, target] = d.anchors;
      // stop = mirror of target (1:1) below entry
      d.anchors.push({ time: entry.time, price: entry.price - (target.price - entry.price) });
    },
  },
  position: {
    id: "position",
    label: "Position (1:3 R/R)",
    points: 2,
    make: (s) => new Position(s),
    initExtra: (d, chart) => {
      const entry = d.anchors[0];
      const t1 = chart.timeOfX(chart.xOfTime(entry.time) + 150);
      d.anchors.push({ time: t1, price: entry.price });
    },
  },
};

const CLASS_BY_TYPE: Record<string, (s: DrawStyle) => DrawingModel> = {
  trendline: (s) => new TrendLine(s),
  ray: (s) => new Ray(s),
  extended: (s) => new ExtendedLine(s),
  arrow: (s) => new Arrow(s),
  hline: (s) => new HorizontalLine(s),
  vline: (s) => new VerticalLine(s),
  rect: (s) => new Rectangle(s),
  ellipse: (s) => new Ellipse(s),
  channel: (s) => new ParallelChannel(s),
  fibretr: (s) => new FibRetracement(s),
  fibext: (s) => new FibExtension(s),
  text: (s) => new TextLabel(s),
  pricerange: (s) => new PriceRange(s),
  daterange: (s) => new DateRange(s),
  riskreward: (s) => new RiskReward(s),
  position: (s) => new Position(s),
  // backward-compat: old saved Long/Short become the unified auto-detecting position
  longpos: (s) => new Position(s),
  shortpos: (s) => new Position(s),
};

export function reviveDrawing(data: any): DrawingModel | null {
  const factory = CLASS_BY_TYPE[data.type];
  if (!factory) return null;
  const d = factory(defaultStyle(data.style?.color ?? "#2962ff"));
  d.loadFrom(data);
  return d;
}
